// src/features/platform/api.ts
import { queryOptions, useQueryClient } from "@tanstack/react-query";

import { api, call } from "@/api/client";
import { useAction } from "@/lib/mutations";

import type {
  AICallLogList, AICallLogStat, AIProvider, AIProviderConfig, AIProviderTestResult, ActionResult, Item, Overview, Paged, PlatformSecretRow,
  ResourceMeta, SettingRow,
} from "./types";

type Query = Record<string, string | number | boolean | undefined>;
const clean = (q: Query): Query => Object.fromEntries(Object.entries(q).filter(([, v]) => v !== undefined && v !== ""));
const P = (resource: string, query?: Query): Record<string, unknown> => ({ params: { path: { resource }, query: query ? clean(query) : undefined } });
const PI = (resource: string, item_id: string, query?: Query): Record<string, unknown> => ({ params: { path: { resource, item_id }, query: query ? clean(query) : undefined } });

export const catalogQuery = queryOptions({
  queryKey: ["platform", "catalog"],
  queryFn: () => call(api.GET("/api/platform/catalog")) as Promise<ResourceMeta[]>,
  staleTime: 5 * 60_000,
});

export const overviewQuery = (integrity = false) =>
  queryOptions({
    queryKey: ["platform", "overview", integrity],
    queryFn: () => call(api.GET("/api/platform/overview", { params: { query: integrity ? { integrity: true } : {} } })) as Promise<Overview>,
    staleTime: 20_000,
  });

export const listQuery = (resource: string, query: Query) =>
  queryOptions({
    queryKey: ["platform", "list", resource, clean(query)],
    queryFn: () => call(api.GET("/api/platform/r/{resource}", P(resource, query) as never)) as unknown as Promise<Paged>,
    placeholderData: (prev) => prev,
  });

export const itemQuery = (resource: string, id: string) =>
  queryOptions({
    queryKey: ["platform", "item", resource, id],
    queryFn: () => call(api.GET("/api/platform/r/{resource}/{item_id}", PI(resource, id) as never)) as unknown as Promise<Item>,
    enabled: !!id,
  });

export const settingsQuery = queryOptions({
  queryKey: ["platform", "settings"],
  queryFn: () => call(api.GET("/api/platform/settings")) as unknown as Promise<SettingRow[]>,
});

const ALL = [["platform"]];

export const usePatchItem = (resource: string, id: string) =>
  useAction((v: { changes: Record<string, unknown>; reason: string }) =>
    call(api.PATCH("/api/platform/r/{resource}/{item_id}", { ...PI(resource, id), body: v } as never)) as unknown as Promise<Item>, { invalidate: ALL, success: "تم حفظ التعديل وتسجيله", silentError: true });

export const useRunAction = (resource: string, id: string) =>
  useAction((v: { action: string; reason: string; params?: Record<string, unknown> }) =>
    call(api.POST("/api/platform/r/{resource}/{item_id}/actions/{action}", { params: { path: { resource, item_id: id, action: v.action } }, body: { reason: v.reason, params: v.params ?? {} } } as never)) as unknown as Promise<ActionResult>,
    { invalidate: ALL, silentError: true });

/** كشف الحقول المقنّعة: يُسجَّل الكشف نفسه، والنتيجة تُعرض في الدرج فقط ولا تُخزَّن في الكاش. */
export function useReveal(resource: string, id: string) {
  const qc = useQueryClient();
  return useAction(async (reason: string) => {
    const data = (await call(api.GET("/api/platform/r/{resource}/{item_id}", PI(resource, id, { reveal: true, reason }) as never))) as unknown as Item;
    void qc.invalidateQueries({ queryKey: ["platform", "list", "action-logs"] });
    return data;
  }, { silentError: true });
}

export const useSaveSetting = () =>
  useAction((v: { key: string; value: string; reason: string }) =>
    call(api.PUT("/api/platform/settings/{key}", { params: { path: { key: v.key } }, body: { value: v.value, reason: v.reason } })), { invalidate: ALL, success: "تم حفظ الإعداد وتسجيله", silentError: true });

export const useCreateOrg = () =>
  useAction((body: { name: string; commercial_registration_no?: string | null; reason: string }) => call(api.POST("/api/platform/organizations", { body })), { invalidate: ALL, success: "تم إنشاء المنظمة", silentError: true });

export const useCreateUser = () =>
  useAction((body: { organization_id: string; full_name: string; phone_number: string; password: string; email?: string | null; roles: { role: string; project_id?: string | null }[]; reason: string }) =>
    call(api.POST("/api/platform/users", { body: body as never })), { invalidate: ALL, success: "تم إنشاء المستخدم", silentError: true });

export const useGrantRole = (userId: string) =>
  useAction((body: { role: string; project_id?: string | null; reason: string }) =>
    call(api.POST("/api/platform/users/{user_id}/roles", { params: { path: { user_id: userId } }, body: body as never })), { invalidate: ALL, success: "تم منح الدور", silentError: true });

export const useRevokeRole = (userId: string) =>
  useAction((v: { roleId: string; reason: string }) =>
    call(api.POST("/api/platform/users/{user_id}/roles/{role_id}/revoke", { params: { path: { user_id: userId, role_id: v.roleId } }, body: { reason: v.reason } })), { invalidate: ALL, success: "تم سحب الدور", silentError: true });

export const useBroadcast = () =>
  useAction((body: { audience: string; message: string; title: string; channel: string; reason: string }) =>
    call(api.POST("/api/platform/notifications/broadcast", { body: body as never })) as unknown as Promise<{ recipients: number }>, { invalidate: ALL, silentError: true });

export type AdapterHealth = {
  name: string; is_mock: boolean; usable: boolean; registry_status: string | null; last_verified_at: string | null;
  breaker: { state: "closed" | "open" | "half_open"; retry_after: number };
  outbox: Record<string, number>; oldest_pending_seconds: number | null; last_success_at: string | null;
};
export type AiHealth = {
  configured: boolean; mock_active: boolean; usable: boolean; model: string | null; provider: string | null;
  letters: { DRAFT: number; ISSUED: number; VOID: number }; mock_generated_count: number;
};
export type IntegrationsHealth = { allow_mock_integrations: boolean; embedded_worker_enabled: boolean; adapters: AdapterHealth[]; ai: AiHealth };

export const integrationsHealthQuery = queryOptions({
  queryKey: ["platform", "integrations-health"],
  queryFn: () => call(api.GET("/api/platform/integrations/health")) as unknown as Promise<IntegrationsHealth>,
  staleTime: 15_000,
});

// ==================== مزوّدو الذكاء الاصطناعي (توليد الخطابات) ====================

const AI = [["platform", "ai-providers"], ["platform", "integrations-health"]];

// ملاحظة: مسارات /api/platform/ai-providers/* أضيفت في الباك إند ولسه ما دخلتش src/api/schema.ts
// (المولَّد من openapi.json — يتولَّد تلقائياً بـ `npm run api:types` بعد نشر الباك إند). لحد التوليد،
// نستخدم (api.GET/POST/PATCH as any) هنا صراحة بدل ما نلوي النوع العام PathsWithMethod، بنفس فكرة
// as never المستخدمة في أماكن تانية بالملف لمسارات ديناميكية.
/* eslint-disable @typescript-eslint/no-explicit-any */
const GET: any = api.GET;
const POST: any = api.POST;
const PATCH: any = api.PATCH;
const PUT: any = api.PUT;
/* eslint-enable @typescript-eslint/no-explicit-any */

export const aiProvidersQuery = queryOptions({
  queryKey: ["platform", "ai-providers"],
  queryFn: () => call(GET("/api/platform/ai-providers")) as unknown as Promise<AIProviderConfig[]>,
});

type AiProviderMutableFields = {
  model_name: string; api_key: string; base_url?: string | null; supports_vision?: boolean; vision_model_name?: string | null;
};

export const useCreateAiProvider = () =>
  useAction((body: AiProviderMutableFields & { name: string; provider: AIProvider; reason: string }) =>
    call(POST("/api/platform/ai-providers", { body })), { invalidate: AI, success: "تم إضافة الإعداد", silentError: true });

export const useUpdateAiProvider = (id: string) =>
  useAction((body: Partial<AiProviderMutableFields> & { name?: string; reason: string }) =>
    call(PATCH(`/api/platform/ai-providers/${id}`, { body })),
    { invalidate: AI, success: "تم حفظ التعديل", silentError: true });

// اختبار اتصال فوري ببيانات المودال نفسها (بلا حفظ) — نفس شكل الحقول المطلوبة وقت الإنشاء بالضبط.
export const useTestAiProvider = () =>
  useAction((body: { provider: AIProvider; model_name: string; api_key: string; base_url?: string | null }) =>
    call(POST("/api/platform/ai-providers/test-connection", { body })) as unknown as Promise<AIProviderTestResult>,
    { silentError: true });

export const useActivateAiProvider = () =>
  useAction((v: { id: string; reason: string }) =>
    call(POST(`/api/platform/ai-providers/${v.id}/activate`, { body: { reason: v.reason } })),
    { invalidate: AI, success: "تم تفعيل المزوّد — كل توليد جديد هيستخدمه فوراً", silentError: true });

export const useDeactivateAiProvider = () =>
  useAction((v: { id: string; reason: string }) =>
    call(POST(`/api/platform/ai-providers/${v.id}/deactivate`, { body: { reason: v.reason } })),
    { invalidate: AI, success: "تم تعطيل الذكاء الاصطناعي — التوليد هيرجع لوضع المحاكاة أو يتوقف", silentError: true });

export const useDeleteAiProvider = () =>
  useAction((v: { id: string; reason: string }) =>
    call(POST(`/api/platform/ai-providers/${v.id}/delete`, { body: { reason: v.reason } })),
    { invalidate: AI, success: "تم حذف الإعداد", silentError: true });

// ==================== سجل نداءات الذكاء الاصطناعي (ai_call_logs — مراجعة نجاح/توافر فقط) ====================
// نفس ملحوظة مسارات ai-providers فوق: /api/platform/ai-providers/call-logs لسه ما دخلتش src/api/schema.ts
// المولَّد، فبنستخدم GET (any) المعرّفة فوق.

export const aiCallLogsQuery = (limit: number, offset: number) =>
  queryOptions({
    queryKey: ["platform", "ai-call-logs", limit, offset],
    queryFn: () => call(GET("/api/platform/ai-providers/call-logs", { params: { query: { limit, offset } } })) as unknown as Promise<AICallLogList>,
    placeholderData: (prev) => prev,
  });

export const aiCallLogStatsQuery = (sinceHours: number | undefined) =>
  queryOptions({
    queryKey: ["platform", "ai-call-log-stats", sinceHours],
    queryFn: () => call(GET("/api/platform/ai-providers/call-logs/stats", { params: { query: clean({ since_hours: sinceHours }) } })) as unknown as Promise<AICallLogStat[]>,
  });

// ==================== الأسرار العامة (R2 / نفاذ / بوابة الدفع / بصمة الهوية / ZATCA) ====================

// ملاحظة: نفس وضع مسارات ai-providers أعلاه — /api/platform/secrets/* لسه ما دخلتش src/api/schema.ts
// المولَّد، فنستخدم GET/POST/PUT (any) المعرّفة فوق.
const SECRETS = [["platform", "secrets"]];

export const platformSecretsQuery = queryOptions({
  queryKey: ["platform", "secrets"],
  queryFn: () => call(GET("/api/platform/secrets")) as unknown as Promise<PlatformSecretRow[]>,
});

export const useSavePlatformSecret = () =>
  useAction((v: { key: string; value: string; reason: string }) =>
    call(PUT(`/api/platform/secrets/${v.key}`, { body: { value: v.value, reason: v.reason } })),
    { invalidate: SECRETS, success: "تم حفظ السرّ وتشفيره", silentError: true });

export const useClearPlatformSecret = () =>
  useAction((v: { key: string; reason: string }) =>
    call(POST(`/api/platform/secrets/${v.key}/clear`, { body: { reason: v.reason } })),
    { invalidate: SECRETS, success: "تم مسح السرّ", silentError: true });
